import type { ReactNode } from "react";
import {
  getCurrencyExponent,
  type SupportedCurrencyCode,
} from "@repo/api-schema";

// Bounded by the API's currency allowlist, so this won't grow without bound.
// If we ever add per-locale variants this becomes a leak — convert to an LRU
// at that point.
const CURRENCY_FORMATTERS = new Map<SupportedCurrencyCode, Intl.NumberFormat>();

function formatterFor(currency: SupportedCurrencyCode): Intl.NumberFormat {
  let f = CURRENCY_FORMATTERS.get(currency);
  if (!f) {
    const exponent = getCurrencyExponent(currency);
    // Pin fraction-digits to the currency's ISO 4217 minor exponent so
    // 0-decimal currencies (JPY, KRW…) and 3-decimal ones (KWD, BHD…)
    // render with the right precision regardless of the runtime locale's
    // default for the code.
    f = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent,
    });
    CURRENCY_FORMATTERS.set(currency, f);
  }
  return f;
}

interface MoneyAmountProps {
  /** Amount in the smallest currency unit. */
  minor: number;
  currency: SupportedCurrencyCode;
  className?: string;
}

export function MoneyAmount({
  minor,
  currency,
  className = "",
}: MoneyAmountProps): ReactNode {
  const formatted = formatterFor(currency).format(
    minor / 10 ** getCurrencyExponent(currency),
  );
  return <span className={className}>{formatted}</span>;
}
